import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useSnackbar } from 'notistack';
import {
  CreateInvestmentIssueRequestBody,
  InvestmentIssueDTO,
  IssueCategory,
  IssueStatus,
  UpdateInvestmentIssueRequestBody,
} from '@inversor-hub/types';
import EurCurrencyField from '@/components/form/EurCurrencyField';
import { DocumentUploadDropzone } from '@/components/form/DocumentUploadDropzone';
import {
  useCreateInvestmentIssue,
  useDeleteInvestmentIssueDocument,
  useDeleteInvestmentIssue,
  useGetInvestmentIssueStats,
  useListInvestmentIssues,
  useUpdateInvestmentIssue,
  useUploadInvestmentIssueDocument,
} from '@/lib/api/investment-issues';
import { formatCurrency, formatDate, formatDateToYYYYMMDD } from '@/utils/misc';

interface IssuesTabProps {
  investmentId: string;
  canEdit: boolean;
}

interface IssueFormData {
  title: string;
  occurredAt: string;
  status: IssueStatus;
  category: IssueCategory;
  costAmount: number | null;
  notes: string;
}

type StatusFilter = IssueStatus | 'ALL';

const categoryLabels: Record<IssueCategory, string> = {
  BREAKDOWN: 'Avería',
  SPECIAL_ASSESSMENT: 'Derrama',
  NON_PAYMENT: 'Impago',
  MAINTENANCE: 'Mantenimiento',
  LEGAL: 'Legal',
  OTHER: 'Otro',
};

const statusLabels: Record<IssueStatus, string> = {
  OPEN: 'Abierta',
  RESOLVED: 'Resuelta',
};

const emptyForm: IssueFormData = {
  title: '',
  occurredAt: formatDateToYYYYMMDD(new Date()),
  status: 'OPEN',
  category: 'OTHER',
  costAmount: null,
  notes: '',
};

function toFormData(issue?: InvestmentIssueDTO): IssueFormData {
  if (!issue) return emptyForm;

  return {
    title: issue.title,
    occurredAt: formatDateToYYYYMMDD(new Date(issue.occurredAt)),
    status: issue.status,
    category: issue.category,
    costAmount: issue.costAmount ?? null,
    notes: issue.notes ?? '',
  };
}

function toRequestBody(
  data: IssueFormData
): CreateInvestmentIssueRequestBody | UpdateInvestmentIssueRequestBody {
  return {
    title: data.title.trim(),
    occurredAt: data.occurredAt,
    status: data.status,
    category: data.category,
    costAmount: data.costAmount,
    notes: data.notes.trim() || null,
  };
}

interface IssueDialogProps {
  open: boolean;
  issue?: InvestmentIssueDTO;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (data: IssueFormData, file?: File) => void;
}

function IssueDialog({ open, issue, isSubmitting, onClose, onSubmit }: IssueDialogProps) {
  const [formData, setFormData] = useState<IssueFormData>(emptyForm);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (open) {
      setFormData(toFormData(issue));
      setSelectedFile(null);
    }
  }, [open, issue]);

  const canSubmit = formData.title.trim().length > 0 && formData.occurredAt.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{issue ? 'Editar incidencia' : 'Nueva incidencia'}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Título"
            value={formData.title}
            onChange={(event) => setFormData((prev) => ({ ...prev, title: event.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Fecha"
            type="date"
            value={formData.occurredAt}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, occurredAt: event.target.value }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Estado"
            value={formData.status}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, status: event.target.value as IssueStatus }))
            }
            select
            fullWidth
          >
            {Object.entries(statusLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Categoría"
            value={formData.category}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, category: event.target.value as IssueCategory }))
            }
            select
            fullWidth
          >
            {Object.entries(categoryLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <EurCurrencyField
            label="Coste"
            value={formData.costAmount}
            onChange={(costAmount) => setFormData((prev) => ({ ...prev, costAmount }))}
          />
          <TextField
            label="Notas"
            value={formData.notes}
            onChange={(event) => setFormData((prev) => ({ ...prev, notes: event.target.value }))}
            multiline
            minRows={3}
            fullWidth
          />
          <DocumentUploadDropzone
            file={selectedFile}
            existingFileName={issue?.document?.fileName}
            onFileChange={setSelectedFile}
            disabled={isSubmitting}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || isSubmitting}
          onClick={() => onSubmit(formData, selectedFile ?? undefined)}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function StatBlock({
  label,
  value,
  isLoading,
}: {
  label: string;
  value: string;
  isLoading: boolean;
}) {
  return (
    <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton width={96} height={32} />
      ) : (
        <Typography variant="h6" fontWeight={700}>
          {value}
        </Typography>
      )}
    </Paper>
  );
}

export const IssuesTab = ({ investmentId, canEdit }: IssuesTabProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState<InvestmentIssueDTO>();
  const [issueToDelete, setIssueToDelete] = useState<InvestmentIssueDTO>();

  const issueStatus = statusFilter === 'ALL' ? undefined : statusFilter;
  const {
    data: issues = [],
    isLoading: isLoadingIssues,
    isError: isIssuesError,
  } = useListInvestmentIssues(investmentId, issueStatus);
  const {
    data: stats,
    isLoading: isLoadingStats,
    isError: isStatsError,
  } = useGetInvestmentIssueStats(investmentId);
  const createMutation = useCreateInvestmentIssue(investmentId);
  const updateMutation = useUpdateInvestmentIssue(investmentId);
  const deleteMutation = useDeleteInvestmentIssue(investmentId);
  const uploadDocumentMutation = useUploadInvestmentIssueDocument(investmentId);
  const deleteDocumentMutation = useDeleteInvestmentIssueDocument(investmentId);

  const handleCreate = () => {
    setEditingIssue(undefined);
    setIsDialogOpen(true);
  };

  const handleEdit = (issue: InvestmentIssueDTO) => {
    setEditingIssue(issue);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (data: IssueFormData, file?: File) => {
    try {
      const payload = toRequestBody(data);
      if (editingIssue) {
        await updateMutation.mutateAsync({ issueId: editingIssue.id, data: payload });
        if (file) {
          await uploadDocumentMutation.mutateAsync({ issueId: editingIssue.id, file });
        }
        enqueueSnackbar('Incidencia actualizada', { variant: 'success' });
      } else {
        const created = await createMutation.mutateAsync(
          payload as CreateInvestmentIssueRequestBody
        );
        if (file) {
          await uploadDocumentMutation.mutateAsync({ issueId: created.id, file });
        }
        enqueueSnackbar('Incidencia creada', { variant: 'success' });
      }
      setIsDialogOpen(false);
    } catch (error) {
      enqueueSnackbar('No se pudo guardar la incidencia', { variant: 'error' });
    }
  };

  const handleUploadDocument = async (issueId: string, file?: File) => {
    if (!file) return;

    try {
      await uploadDocumentMutation.mutateAsync({ issueId, file });
      enqueueSnackbar('Documento adjuntado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('No se pudo adjuntar el documento', { variant: 'error' });
    }
  };

  const handleDeleteDocument = async (issueId: string) => {
    try {
      await deleteDocumentMutation.mutateAsync(issueId);
      enqueueSnackbar('Documento eliminado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('No se pudo eliminar el documento', { variant: 'error' });
    }
  };

  const handleViewDocument = (signedUrl: string) => {
    window.open(signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDelete = async () => {
    if (!issueToDelete) return;
    try {
      await deleteMutation.mutateAsync(issueToDelete.id);
      enqueueSnackbar('Incidencia eliminada', { variant: 'success' });
      setIssueToDelete(undefined);
    } catch (error) {
      enqueueSnackbar('No se pudo eliminar la incidencia', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Incidencias
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registra eventos operativos y costes asociados a esta inversión.
          </Typography>
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Nueva incidencia
          </Button>
        )}
      </Box>

      {isStatsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          No se pudieron cargar los totales de incidencias.
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <StatBlock
            label="Abiertas"
            value={`${stats?.openCount ?? 0}`}
            isLoading={isLoadingStats}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatBlock
            label="Coste año en curso"
            value={formatCurrency(stats?.currentYearCostTotal ?? 0)}
            isLoading={isLoadingStats}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatBlock
            label="Coste histórico"
            value={formatCurrency(stats?.historicalCostTotal ?? 0)}
            isLoading={isLoadingStats}
          />
        </Grid>
      </Grid>

      <Box mb={2}>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          size="small"
          onChange={(_event, value: StatusFilter | null) => {
            if (value) setStatusFilter(value);
          }}
        >
          <ToggleButton value="ALL">Todas</ToggleButton>
          <ToggleButton value="OPEN">Abiertas</ToggleButton>
          <ToggleButton value="RESOLVED">Resueltas</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isIssuesError ? (
        <Alert severity="error">No se pudieron cargar las incidencias.</Alert>
      ) : isLoadingIssues ? (
        <Skeleton variant="rectangular" height={280} />
      ) : issues.length === 0 ? (
        <Alert severity="info">No hay incidencias registradas para el filtro seleccionado.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {issues.map((issue) => (
            <Paper key={issue.id} elevation={1} sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <Box minWidth={0}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {issue.title}
                    </Typography>
                    <Chip
                      label={statusLabels[issue.status]}
                      size="small"
                      color={issue.status === 'OPEN' ? 'warning' : 'success'}
                    />
                    <Chip label={categoryLabels[issue.category]} size="small" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(issue.occurredAt)}
                    {issue.costAmount != null ? ` - ${formatCurrency(issue.costAmount)}` : ''}
                  </Typography>
                  {issue.notes && (
                    <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                      {issue.notes}
                    </Typography>
                  )}
                  {issue.document && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<AttachFileIcon />}
                      sx={{ mt: 1, px: 0 }}
                      onClick={() => handleViewDocument(issue.document!.signedUrl)}
                    >
                      {issue.document.fileName}
                    </Button>
                  )}
                </Box>
                {canEdit && (
                  <Box display="flex" flexShrink={0}>
                    <Tooltip title={issue.document ? 'Reemplazar documento' : 'Adjuntar documento'}>
                      <IconButton component="label" size="small">
                        <UploadFileIcon fontSize="small" />
                        <input
                          hidden
                          type="file"
                          accept="application/pdf,image/*"
                          onChange={(event) =>
                            handleUploadDocument(issue.id, event.target.files?.[0])
                          }
                        />
                      </IconButton>
                    </Tooltip>
                    {issue.document && (
                      <Tooltip title="Eliminar documento">
                        <IconButton
                          size="small"
                          disabled={deleteDocumentMutation.isPending}
                          onClick={() => handleDeleteDocument(issue.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEdit(issue)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => setIssueToDelete(issue)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      <IssueDialog
        open={isDialogOpen}
        issue={editingIssue}
        isSubmitting={
          createMutation.isPending || updateMutation.isPending || uploadDocumentMutation.isPending
        }
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={!!issueToDelete}
        onClose={() => setIssueToDelete(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar incidencia</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ¿Seguro que quieres eliminar la incidencia &#34;{issueToDelete?.title}&#34;? Esta acción
            no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueToDelete(undefined)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
